// External tuning config (game.ini in public/, served at /game.ini).
// loadConfig() fetches + parses it, coerces every field with a fallback, and
// NEVER throws — a missing or malformed file yields the built-in defaults so
// the game always boots. Edit the ini and refresh to retune (no rebuild in dev).

import { parse as parseIni } from "ini";

export interface GameConfig {
  weapon: {
    magazine_size: number;
    reload_time: number; // seconds; firing locked during reload
    pellet_radius: number; // px of aim forgiveness (0 = exact point)
  };
  spawning: {
    spawn_interval_start: number; // seconds between spawns at start
    spawn_interval_min: number; // hardest (fastest) spawn rate
    max_zombies_start: number;
    max_zombies_max: number;
  };
  difficulty: {
    crawl_time_start: number; // window time before falling (start)
    crawl_time_min: number; // hardest
    walker_traverse_start: number; // fall -> reaches player (start)
    walker_traverse_min: number; // hardest
    wave_length: number; // seconds per wave
    ramp_seconds: number; // start -> hardest interpolation window
    stage1_seconds: number; // window-only stage before zombies fall/walk
  };
  scoring: {
    window_kill: number;
    headshot: number;
    combo_enabled: boolean;
  };
  boss: {
    first_kills: number; // running kills before the first boss
    kill_interval: number; // extra kills before each subsequent boss
    base_hp: number; // boss energy at level 1
    hp_per_level: number; // added energy per later boss
    head_damage: number; // energy drained by a headshot
    body_damage: number; // energy drained by a body shot
    move_speed: number; // px/s side-to-side
    wave_interval_start: number; // seconds between summoned waves (level 1)
    wave_interval_min: number; // fastest (scales down with level)
    wave_size: number; // walkers per summoned wave (+ ~1 per 2 levels)
    wave_progress: number; // startProgress so waves begin closer to the player
    axe_interval_min: number; // random gap between thrown axes (seconds)
    axe_interval_max: number;
    axe_flight: number; // seconds for an axe to reach the player (level 1)
    axe_score: number; // points for shooting an axe out of the air
    kill_score: number; // reward = kill_score * boss level
  };
  gameover: {
    blood_fill_seconds: number; // top-to-bottom blood fill duration
    highscore_delay: number; // seconds before reloading to high-score screen
    hard_reload: boolean; // true = location.reload(); false = scene transition
  };
  highscore: {
    table_size: number;
    name_length: number;
  };
}

// Built-in defaults — the single source of truth, mirrored by public/game.ini.
export const DEFAULT_CONFIG: GameConfig = {
  weapon: {
    magazine_size: 5,
    reload_time: 1.4,
    pellet_radius: 4,
  },
  spawning: {
    spawn_interval_start: 1.8,
    spawn_interval_min: 0.6,
    max_zombies_start: 2,
    max_zombies_max: 5,
  },
  difficulty: {
    crawl_time_start: 2.2,
    crawl_time_min: 1.1,
    walker_traverse_start: 5.0,
    walker_traverse_min: 2.8,
    wave_length: 20,
    ramp_seconds: 120,
    stage1_seconds: 30,
  },
  scoring: {
    window_kill: 100,
    headshot: 250,
    combo_enabled: true,
  },
  boss: {
    first_kills: 60,
    kill_interval: 90,
    base_hp: 180,
    hp_per_level: 70,
    head_damage: 25,
    body_damage: 3,
    move_speed: 70,
    wave_interval_start: 4.5,
    wave_interval_min: 2.3,
    wave_size: 2,
    wave_progress: 0.45,
    axe_interval_min: 3.5,
    axe_interval_max: 5,
    axe_flight: 1.7,
    axe_score: 150,
    kill_score: 3000,
  },
  gameover: {
    blood_fill_seconds: 2.5,
    highscore_delay: 60,
    hard_reload: false,
  },
  highscore: {
    table_size: 5,
    name_length: 6,
  },
};

// A parsed ini section is an untyped bag of strings; coerce per field.
type IniBag = Record<string, unknown>;

// Coerce a value to a finite number, else return the default.
function num(bag: IniBag | undefined, key: string, fallback: number): number {
  const raw = bag?.[key];
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const n = Number.parseFloat(raw.trim());
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

// Coerce a value to a boolean, accepting ini-style true/false/1/0/yes/no.
function bool(bag: IniBag | undefined, key: string, fallback: boolean): boolean {
  const raw = bag?.[key];
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") {
    const v = raw.trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes" || v === "on") return true;
    if (v === "false" || v === "0" || v === "no" || v === "off") return false;
  }
  return fallback;
}

// Build a fully-typed config from a loosely-parsed ini object, falling back to
// DEFAULT_CONFIG for any missing or unparseable field.
function coerceConfig(parsed: Record<string, IniBag>): GameConfig {
  const d = DEFAULT_CONFIG;
  const w = parsed.weapon;
  const s = parsed.spawning;
  const f = parsed.difficulty;
  const sc = parsed.scoring;
  const bo = parsed.boss;
  const go = parsed.gameover;
  const hs = parsed.highscore;
  return {
    weapon: {
      magazine_size: num(w, "magazine_size", d.weapon.magazine_size),
      reload_time: num(w, "reload_time", d.weapon.reload_time),
      pellet_radius: num(w, "pellet_radius", d.weapon.pellet_radius),
    },
    spawning: {
      spawn_interval_start: num(s, "spawn_interval_start", d.spawning.spawn_interval_start),
      spawn_interval_min: num(s, "spawn_interval_min", d.spawning.spawn_interval_min),
      max_zombies_start: num(s, "max_zombies_start", d.spawning.max_zombies_start),
      max_zombies_max: num(s, "max_zombies_max", d.spawning.max_zombies_max),
    },
    difficulty: {
      crawl_time_start: num(f, "crawl_time_start", d.difficulty.crawl_time_start),
      crawl_time_min: num(f, "crawl_time_min", d.difficulty.crawl_time_min),
      walker_traverse_start: num(f, "walker_traverse_start", d.difficulty.walker_traverse_start),
      walker_traverse_min: num(f, "walker_traverse_min", d.difficulty.walker_traverse_min),
      wave_length: num(f, "wave_length", d.difficulty.wave_length),
      ramp_seconds: num(f, "ramp_seconds", d.difficulty.ramp_seconds),
      stage1_seconds: num(f, "stage1_seconds", d.difficulty.stage1_seconds),
    },
    scoring: {
      window_kill: num(sc, "window_kill", d.scoring.window_kill),
      headshot: num(sc, "headshot", d.scoring.headshot),
      combo_enabled: bool(sc, "combo_enabled", d.scoring.combo_enabled),
    },
    boss: {
      first_kills: num(bo, "first_kills", d.boss.first_kills),
      kill_interval: num(bo, "kill_interval", d.boss.kill_interval),
      base_hp: num(bo, "base_hp", d.boss.base_hp),
      hp_per_level: num(bo, "hp_per_level", d.boss.hp_per_level),
      head_damage: num(bo, "head_damage", d.boss.head_damage),
      body_damage: num(bo, "body_damage", d.boss.body_damage),
      move_speed: num(bo, "move_speed", d.boss.move_speed),
      wave_interval_start: num(bo, "wave_interval_start", d.boss.wave_interval_start),
      wave_interval_min: num(bo, "wave_interval_min", d.boss.wave_interval_min),
      wave_size: num(bo, "wave_size", d.boss.wave_size),
      wave_progress: num(bo, "wave_progress", d.boss.wave_progress),
      axe_interval_min: num(bo, "axe_interval_min", d.boss.axe_interval_min),
      axe_interval_max: num(bo, "axe_interval_max", d.boss.axe_interval_max),
      axe_flight: num(bo, "axe_flight", d.boss.axe_flight),
      axe_score: num(bo, "axe_score", d.boss.axe_score),
      kill_score: num(bo, "kill_score", d.boss.kill_score),
    },
    gameover: {
      blood_fill_seconds: num(go, "blood_fill_seconds", d.gameover.blood_fill_seconds),
      highscore_delay: num(go, "highscore_delay", d.gameover.highscore_delay),
      hard_reload: bool(go, "hard_reload", d.gameover.hard_reload),
    },
    highscore: {
      table_size: num(hs, "table_size", d.highscore.table_size),
      name_length: num(hs, "name_length", d.highscore.name_length),
    },
  };
}

// Fetch + parse public/game.ini, coercing to a typed GameConfig. Returns the
// built-in defaults (and logs a warning) on any failure. Never throws.
export async function loadConfig(path = "game.ini"): Promise<GameConfig> {
  try {
    const res = await fetch(path, { cache: "no-cache" });
    if (!res.ok) {
      console.warn(`[config] ${path} -> HTTP ${res.status}; using defaults.`);
      return DEFAULT_CONFIG;
    }
    const txt = await res.text();
    const parsed = parseIni(txt) as Record<string, IniBag>;
    return coerceConfig(parsed);
  } catch (err) {
    console.warn(`[config] failed to load ${path}; using defaults.`, err);
    return DEFAULT_CONFIG;
  }
}
